/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/use-unknown-in-catch-callback-variable */

import { useEffect } from 'react';

import { skipToken, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import Toast from '@/components/ui/Toast';
import { appConfig } from '@/config/appConfig';
import { queryClient } from '@/config/queryClient';
import { cleanObject } from '@/helpers';
import type { UseGetResult } from '@/types/UseGetResult';

import useAxios from './useAxios';

type UseGetOptions = {
    keys?: string[];
    queryParams?: Record<string, unknown>;
    enabled?: boolean;
    clearOnUnmount?: boolean;
    clearOnRefetch?: boolean;
    refetchInterval?: number;
};

export default function useGet<TData = unknown, TError = unknown>(
    url: string | null,
    {
        keys,
        queryParams,
        enabled = true,
        clearOnUnmount = appConfig.http.get.clearOnUnmount,
        clearOnRefetch = appConfig.http.get.clearOnRefetch,
        refetchInterval,
    }: UseGetOptions = {}
): UseGetResult<TData, TError> {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const { axiosInstance } = useAxios();

    const params = cleanObject(queryParams);

    const { data, error, isLoading, isFetching, isRefetching, isFetched, isPending, isSuccess, isPaused, isStale, isError, refetch } =
        useQuery({
            queryKey: keys ?? [url],
            queryFn: url
                ? (): Promise<unknown> =>
                    axiosInstance
                        .get(url, {
                            params,
                        })
                        .then((res) => res.data)
                        .catch((err: AxiosError) => {
                            const errorMessage = (err.response?.data as { message?: string }).message;
                            Toast.error({ message: errorMessage ?? err.message });
                            return err;
                        })
                : skipToken,
            enabled: !!url && enabled,
            refetchInterval,
        });

    // ----------------------------------------------------------------------------------------------------
    // MARK: Side Effects
    // ----------------------------------------------------------------------------------------------------
    useEffect(() => {
        if (clearOnRefetch) {
            queryClient.removeQueries({
                queryKey: [url],
                exact: false,
            });
        }
        refetch();
    }, [url, JSON.stringify(queryParams), clearOnRefetch]);

    useEffect(() => {
        if (clearOnUnmount) {
            return (): void => {
                queryClient.removeQueries({
                    queryKey: [url],
                    exact: false,
                });
            };
        }
    }, [queryClient, url]);

    // ----------------------------------------------------------------------------------------------------
    // MARK: Return
    // ----------------------------------------------------------------------------------------------------
    return {
        response: data as TData,
        error: error as TError,
        isLoading,
        isFetching,
        isRefetching,
        isFetched,
        isPending,
        isSuccess,
        isPaused,
        isStale,
        isError,
    };
}
